// 職責：全站路由表與側欄導覽模型的單一事實來源（doc 06 §8.1）

/*
 * 三條設計決定，都是為了修掉舊版的具體毛病：
 *
 * ① 子分頁走 query param（`?tab=`）而非子路由
 *    舊版把「收款方式／折扣／現金券／推廣優惠／簽賬」拆成五條獨立路由，於是它們共用的
 *    工具列、篩選與批次條被複製了五份。改成 ?tab= 之後，一個 view 一份狀態，
 *    而且分頁仍可分享、可用瀏覽器上一頁回退。
 *
 * ② 抽屜走路由（`/menu/:id`）
 *    深連結（把某個餐品的編輯畫面貼給同事）與「返回鍵應該關抽屜而不是離開頁面」
 *    這兩件事，只有把抽屜狀態放進 URL 才會自然成立。
 *
 * ③ 全部 lazy load
 *    ECharts 與匯出用的 exceljs 都很大，登入與概覽不該為了報表付這個載入成本（doc 06 §10.4）。
 *
 * ✅ 2026-08-07：九個模組**全部**接上真 view，`placeholder` 只剩 403／404 兩個系統頁在用。
 *
 * ④ 權限（authz v2，2026-09-03）：每條 shell 路由的 `meta.permission` 是目錄 key
 *    （`<module>:view`），側欄 `NAV_SECTIONS` 由 `domain/permissions/catalog` 派生 ——
 *    標籤／圖示／分組只有一個來源，側欄改名不會與權限樹、ops-console 範本頁各叫一套。
 *    舊的 `ANY_PERMISSION`（「有任何一個按鈕碼就進得去」）與「權限表空就全放行」兩條後備
 *    一起刪除：權限現在是目錄 key，不再靠舊路由名對照；空集就是確實什麼都沒有（D7）。
 */

import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router'

import {
  CATALOG,
  isKnown,
  moduleViewKey,
  type ModuleKey,
  type NavGroup,
  type PermissionKey,
} from '@/domain/permissions/catalog'
import { hasPermission } from '@/domain/permissions/permissionSet'
import type { IconName } from '@/shared/ui'

/* ────────────────────────────── 路由 meta 契約 ────────────────────────────── */

declare module 'vue-router' {
  interface RouteMeta {
    /** 免認證（登入頁、404）。未標示者一律需要完整門店 session */
    public?: boolean
    /** ★ 只需 ops 集團層 session（選店頁）—— 此時尚無門店 token，用 isAuthed 會把使用者彈回登入頁 */
    opsOnly?: boolean
    /**
     * 進入所需的權限 key（目錄字面聯集；打錯字會觸發 vue-tsc 錯誤）。未指定表示只要登入就能進。
     * 模組路由一律是 `<module>:view`；分頁層另由 view 用 `useModuleTabs` 把關。
     */
    permission?: PermissionKey
    /**
     * ★ 這條路由屬於哪個模組。預設就是路由自己（模組路由 name = module）。
     *
     * 同一個模組可以有多條路由 —— 例如編輯抽屜的深連結 `menu/:id` 名叫 `menu-item`。
     * 2026-09-03 浸會 hkbu$11 確實遇過「菜單管理進得去，一按編輯就說沒有權限」：
     * 舊版拿 route name 去查以模組名為 key 的權限表。現在 `permission` 已經是目錄 key，
     * 守衛不再需要這一欄查表；保留它是讓 `routes.spec` 斷言「深連結的 permission
     * 一定等於所屬模組的 view key」，使這種組合不會再靜默地出現。
     */
    permissionOf?: ModuleKey
    /** 瀏覽器標題與 PlaceholderView 顯示用的模組名（繁體中文） */
    title?: string
  }
}

/* ────────────────────────────── 返回 URL ────────────────────────────── */

/** 被守衛攔下時記住原本目的地的 query 鍵；守衛（寫入）與登入流程（讀取）必須共用同一個常數 */
export const REDIRECT_QUERY = 'redirect'

/**
 * 把 `?redirect=` 正規化成安全的站內路徑（做法採自 ops-console，doc 09 §8.2）。
 *
 * ⚠️ 這個值來自 URL，是**使用者可控輸入**。不驗證就 push 出去等於開放轉址：
 *   `/login?redirect=https://evil.example` 會讓釣魚站看起來是從本站跳過去的。
 *   規則：必須以單一 `/` 開頭（`//host` 在瀏覽器眼中是 protocol-relative 的外部網址）。
 */
export function sanitizeRedirect(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  if (!raw.startsWith('/') || raw.startsWith('//')) return null
  // 反斜線會被部分瀏覽器正規化成斜線，`/\evil.example` 因此可能逸出
  if (raw.includes('\\')) return null
  return raw
}

/** 登入完成後該去哪：優先回到當初被攔下的頁面，否則進概覽 */
export function resolveAfterLogin(route: RouteLocationNormalized): string {
  return sanitizeRedirect(route.query[REDIRECT_QUERY]) ?? '/dashboard'
}

/* ────────────────────────────── 權限判定 ────────────────────────────── */

/**
 * 路由／選單項是否對當前使用者開放。
 * 側欄過濾與 router 守衛**必須共用這個函式** —— 兩套判斷會產生「選單看得到但點進去 403」
 * 這種最令人困惑的組合。
 *
 * ★ 沒有「權限集空就放行」分支（D7）。2026-09-02 之前那個後備是因為店碼還是舊路由名、
 *   對不上就會全部消失；現在店碼由 ops-console 按同一份目錄將閉包寫入店庫，對不上 = 確實未授權。
 *   空側欄是「要找管理員開權限」的訊號，不再是「資料未遷移」的雜訊。
 * ★ 前端閘與門店 webapi `[RequirePermission]` 檢查同一批店碼 —— 後端初期 log 模式，
 *   soak 完才 enforce（plan §7.2）。在那之前，這裡仍然只是「不讓人走進一個必然失敗的頁面」。
 */
export function isRouteAllowed(
  permission: PermissionKey | undefined,
  permissions: ReadonlySet<PermissionKey>,
  isSuperUser: boolean,
): boolean {
  if (permission === undefined) return true
  return hasPermission(permissions, permission, isSuperUser)
}

/**
 * 模組**裡面某一條分頁**是否開放（2026-09-03 加）。
 *
 * 為什麼要有分頁層：「改餐牌」要用到「系統設定 → 改碼造法」，但同一個模組裡面還有
 * 門店資料及 YeahPay 收單（收單裝置設定）。為了一條改碼造法而開放整個模組，
 * 等於順帶把收單設定也開放了。
 *
 * 規則：要有 `<module>.<section>:view`（授予任何一個分頁動作，閉包都會一併帶出它）。
 * ★ 模組 view **不會**帶出全部分頁（見 catalog.ts 檔頭 ★）—— 只有 `shopmgr-settings`
 *   一列的舊店會見到模組但沒有分頁，要 ops-console resync 才補齊；這是已知的升級次序。
 * ★ 目錄沒有這條分頁 ⇒ false（fail-closed）：`TAB_VALUES` 與目錄的一致性另有測試把關。
 */
export function isSectionAllowed(
  module: ModuleKey,
  section: string,
  permissions: ReadonlySet<PermissionKey>,
  isSuperUser: boolean,
): boolean {
  const key = `${module}.${section}:view`
  if (!isKnown(key)) return false
  return hasPermission(permissions, key, isSuperUser)
}

/* ────────────────────────────── 側欄導覽模型 ────────────────────────────── */

export interface NavItem {
  /** 對應路由 name（= 目錄 module） */
  name: string
  label: string
  icon: IconName
  permission?: PermissionKey
  /**
   * 只有零售店才顯示（判定與 `term.goods`「商品／餐品」使用同一條規則：`isRetailBusiness`）。
   * 與 permission 是兩回事：這個是「這間店用不用得著」，不是「這個人有沒有權」。
   */
  retailOnly?: boolean
}

export interface NavSection {
  /** 分組標題；省略表示置頂的無分組區（概覽） */
  label?: string
  items: readonly NavItem[]
}

/** 三個分組的次序是版面決定，目錄只說明「屬於哪一組」 */
const NAV_GROUP_ORDER: readonly NavGroup[] = ['營運', '洞察', '設定']

/**
 * 只有零售店才出現在側欄的模組（見 `NavItem.retailOnly`）。
 * 放在這裡而不寫進權限目錄：這是門店業務屬性，不是權限；目錄匯出的 JSON 亦因此與
 * ops-console `control-plane/shopmgr-permissions.json` 保持逐 byte 一致。
 */
const RETAIL_ONLY_MODULES: ReadonlySet<ModuleKey> = new Set<ModuleKey>(['retail-goods'])

/**
 * 側欄分組（doc 03 §2.2 / doc 00 §3 的 M1–M10）。
 *
 * ★ 九個模組項由 `CATALOG` 派生，不另抄一份：標籤與權限樹（ops-console 範本頁、
 *   員工模組矩陣）是同一個字串，改一處全站跟隨。概覽不是目錄模組（沒有權限、沒有 ops 列），
 *   所以是唯一手寫的那一項。
 *   `ModuleIcon` 是 `IconName` 的子集，型別上直接賦值 —— 目錄寫錯圖示名會觸發 vue-tsc 錯誤。
 */
export const NAV_SECTIONS: readonly NavSection[] = [
  {
    items: [{ name: 'dashboard', label: '概覽', icon: 'i-grid' }],
  },
  ...NAV_GROUP_ORDER.map<NavSection>((group) => ({
    label: group,
    items: CATALOG.flatMap<NavItem>((m) =>
      m.navGroup === group && m.route !== null && m.icon !== null
        ? [
            {
              name: m.route,
              label: m.label,
              icon: m.icon,
              permission: moduleViewKey(m.module),
              ...(RETAIL_ONLY_MODULES.has(m.module) ? { retailOnly: true } : {}),
            },
          ]
        : [],
    ),
  })),
]

/* ────────────────────────────── 路由表 ────────────────────────────── */

/*
 * 佔位頁。九個模組已全部接上真 view，這裡只剩 403 與 404 兩個系統頁在用。
 * 同一個 `import()` 表達式在 Rollup 下會被去重為單一 chunk。
 */
const placeholder = () => import('@/app/views/PlaceholderView.vue')

const appShell = () => import('@/app/layouts/AppShell.vue')
const authLayout = () => import('@/app/layouts/AuthLayout.vue')

export const routes: readonly RouteRecordRaw[] = [
  {
    path: '/',
    component: appShell,
    // 沒有 '' 子路由：進 '/' 一律轉概覽，避免出現一個沒有內容的殼
    redirect: { name: 'dashboard' },
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/features/dashboard/views/DashboardView.vue'),
        meta: { title: '營運概覽' },
      },
      {
        path: 'menu',
        name: 'menu',
        component: () => import('@/features/menu/views/MenuView.vue'),
        meta: { title: '菜單管理', permission: 'menu:view' },
      },
      {
        // ★ 編輯抽屜的深連結（doc 06 §8.1）。與 'menu' 是同一個 view，
        //   由 view 依 route.params.id 決定抽屜開闔（id=0 是新增），不是另一個頁面。
        path: 'menu/:id',
        name: 'menu-item',
        component: () => import('@/features/menu/views/MenuView.vue'),
        // permissionOf: 與 'menu' 共用權限 —— 它本來就是同一個模組、同一個 view
        meta: { title: '菜單管理', permission: 'menu:view', permissionOf: 'menu' },
      },
      {
        // ?tab=dinner|takeaway，展開明細走 ?period=<id>（同一條路由內，不開子路由）
        path: 'periods',
        name: 'periods',
        component: () => import('@/features/periods/views/PeriodsView.vue'),
        meta: { title: '供應時段', permission: 'periods:view' },
      },
      {
        /*
         * M12 零售商品管理 —— 菜單管理的零售簡化版，維護的欄位與商品匯入一樣。
         * ★ 自己一個模組（2026-09-12 甲方：ops 角色範本要選得到「商品管理」）：
         *   店庫標記列 `shopmgr-retail-goods`（uri = retail-goods），由 ops-console 權限目錄
         *   `retail-goods:view|add|edit|delete` 同步到門店。後端 `retail/save` 認
         *   `shopmgr-retail-goods:edit/add`；刪除走共用的 `remove/{id}`，菜單或商品管理的 delete 都可以。
         *   🚨 之前借 'menu' 的權限（permissionOf）。改了之後，店庫未同步到新標記列的受限帳號
         *      會見不到這頁 —— 部署前 ops-console 要先「重推」角色範本。
         *   側欄只有零售店才見到（NavItem.retailOnly）。
         * 正在選的品牌走 ?category=<id>（與 menu 一樣，重新整理不會跳走）。
         */
        path: 'retail-goods',
        name: 'retail-goods',
        component: () => import('@/features/retail-goods/views/RetailGoodsView.vue'),
        meta: { title: '商品管理', permission: 'retail-goods:view' },
      },
      {
        /*
         * M11 零售商品匯入。
         * ★ 2026-09-02 由「不設 permission」改為設閘：這一頁**會寫入商品**，不應該對
         *   「報表唯讀」之類的帳號開放。沒有 `shopmgr-goods-import` 列的門店，非超級用戶
         *   進不去是預期的行為 —— 要重新開放就在 ops-console 授予 `goods-import:view`，而不是拿走這個閘。
         *   試算（dry-run）只需要 view；正式上傳另外需要 `goods-import:apply`。
         */
        path: 'goods-import',
        name: 'goods-import',
        component: () => import('@/features/goods-import/views/GoodsImportView.vue'),
        meta: { title: '商品匯入', permission: 'goods-import:view' },
      },
      {
        /*
         * 已清機補撇數（2026-10-06）：清機後 POS 不能再撇數，這頁為已清機的訂單補撇、重撇或撤銷。
         * 進入只需 view；三種寫入都要 `deptsplit-his:edit`（webapi 認 `shopmgr-deptsplit-his:edit`）。
         */
        path: 'deptsplit-his',
        name: 'deptsplit-his',
        component: () => import('@/features/deptsplit-his/views/DeptSplitHisView.vue'),
        meta: { title: '已清機補撇數', permission: 'deptsplit-his:view' },
      },
      {
        // ?tab=combo|addon|drink|flavour，跟餐包內容層走 ?pkg=<id>（同一條路由內）
        path: 'combos',
        name: 'combos',
        component: () => import('@/features/combos/views/CombosView.vue'),
        meta: { title: '套餐搭配', permission: 'combos:view' },
      },
      {
        // ?tab=goods|payment|items
        path: 'reports',
        name: 'reports',
        component: () => import('@/features/reports/views/ReportsView.vue'),
        // ★ 2026-09-02 與 goods-import 一起加閘，理由見上面那一段
        meta: { title: '報表分析', permission: 'reports:view' },
      },
      {
        // ?tab=methods|discount|promo
        path: 'payments',
        name: 'payments',
        component: () => import('@/features/payments/views/PaymentsView.vue'),
        meta: { title: '收款與優惠', permission: 'payments:view' },
      },
      {
        // ?tab=printers|depts|tables|pickup
        path: 'kitchen',
        name: 'kitchen',
        component: () => import('@/features/kitchen/views/KitchenView.vue'),
        meta: { title: '廚房與打印', permission: 'kitchen:view' },
      },
      {
        // ?tab=staff|roles|access|audit
        path: 'employees',
        name: 'employees',
        component: () => import('@/features/employees/views/EmployeesView.vue'),
        meta: { title: '員工與權限', permission: 'employees:view' },
      },
      {
        // ?tab=dict|shopinfo|jobs|yeahpay（每條分頁各自要 `settings.<tab>:view`）
        path: 'settings',
        name: 'settings',
        component: () => import('@/features/settings/views/SettingsView.vue'),
        meta: { title: '系統設定', permission: 'settings:view' },
      },
    ],
  },

  /*
   * 認證流程的三個畫面共用 AuthLayout。子路由用絕對路徑，讓 URL 維持扁平
   * （`/login` 而非 `/auth/login`）—— 舊版的書籤與 nginx 設定都指向 `/login`。
   */
  {
    path: '/',
    component: authLayout,
    children: [
      {
        path: '/login',
        name: 'login',
        component: () => import('@/features/auth/views/LoginView.vue'),
        meta: { title: '集團登入', public: true },
      },
      {
        path: '/quick-access',
        name: 'quick-access',
        component: () => import('@/features/auth/views/QuickAccessView.vue'),
        meta: { title: '快速開啟報表', public: true },
      },
      {
        // 選店：只有 ops session，尚無門店 token（doc 09 §3.3 步驟 ②）
        path: '/choose-shop',
        name: 'choose-shop',
        component: () => import('@/features/auth/views/ChooseShopView.vue'),
        meta: { title: '選擇門店', opsOnly: true },
      },
      {
        path: '/403',
        name: 'forbidden',
        component: placeholder,
        meta: { title: '沒有權限' },
      },
      {
        // 舊版的 not-accept.vue 實為 403 fallback，新版正名為 404（doc 00 §3 不遷移清單）
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: placeholder,
        meta: { title: '找不到頁面', public: true },
      },
    ],
  },
]

/*
 * 開發期一致性斷言：側欄列出的路由名必須真的存在於路由表，
 * 且權限標註要一致。少了它，改名之後只會在點擊時才炸，而且是靜默地什麼都不發生。
 * production 建置時整段會被 dead-code 消除。
 */
if (import.meta.env.DEV) {
  const shellChildren = routes[0]?.children ?? []
  const byName = new Map(shellChildren.map((r) => [String(r.name), r.meta?.permission]))
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (!byName.has(item.name)) {
        console.error(`[routes] 側欄項目 "${item.name}" 沒有對應的路由`)
      } else if (byName.get(item.name) !== item.permission) {
        console.error(`[routes] 側欄項目 "${item.name}" 的 permission 與路由 meta 不一致`)
      }
    }
  }
}

export default routes
